import { useEffect, useMemo, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, SlidersHorizontal, ChevronDown } from "lucide-react";
import { CATEGORIES, byCategory, fmtPrice } from "@/data/catalog";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = ["relevance", "nameAZ", "nameZA", "priceAsc", "priceDesc"];
const sortFn = { nameAZ: (a, b) => a.name.localeCompare(b.name), nameZA: (a, b) => b.name.localeCompare(a.name), priceAsc: (a, b) => a.price - b.price, priceDesc: (a, b) => b.price - a.price };

export default function Category() {
  const { cat } = useParams();
  const { t, i18n } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const category = CATEGORIES.find((c) => c.id === cat);
  const sub = sp.get("sub");
  const brand = sp.get("brand");
  const sort = sp.get("sort") || "relevance";
  const maxP = sp.get("max");
  const minP = sp.get("min");
  const [showF, setShowF] = useState(false);
  const [range, setRange] = useState([0, 0]);

  const all = useMemo(() => byCategory(cat), [cat]);
  const brands = useMemo(() => [...new Set(all.map((p) => p.brand))].sort(), [all]);
  const top = useMemo(() => Math.ceil(Math.max(...all.map((p) => p.price), 1)), [all]);
  const inPrice = (p) => (!maxP || p.price <= Number(maxP)) && (!minP || p.price >= Number(minP));
  const subsAvail = useMemo(() => category ? category.subs.filter((s) => all.some((p) => p.sub === s && (!brand || p.brand === brand) && inPrice(p))) : [], [all, brand, minP, maxP, category]);
  const brandCounts = useMemo(() => {
    const m = {};
    all.forEach((p) => { if ((!sub || p.sub === sub) && inPrice(p)) m[p.brand] = (m[p.brand] || 0) + 1; });
    return m;
  }, [all, sub, minP, maxP]);
  const brandsAvail = brands.filter((b) => brandCounts[b] || b === brand);
  const subsShown = sub && !subsAvail.includes(sub) ? [sub, ...subsAvail] : subsAvail;
  const list = useMemo(() => {
    let l = all.filter((p) => (!sub || p.sub === sub) && (!brand || p.brand === brand) && (!maxP || p.price <= Number(maxP)) && (!minP || p.price >= Number(minP)));
    if (sortFn[sort]) l = [...l].sort(sortFn[sort]);
    return l;
  }, [all, sub, brand, sort, maxP, minP]);

  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  useEffect(() => { setRange([Number(minP || 0), Number(maxP || top)]); }, [minP, maxP, top]);
  const commitRange = ([lo, hi]) => {
    const n = new URLSearchParams(sp);
    lo > 0 ? n.set("min", String(lo)) : n.delete("min");
    hi < top ? n.set("max", String(hi)) : n.delete("max");
    setSp(n);
  };
  if (!category) return <div className="nv-container py-24 text-center">404</div>;
  const hasFilters = sub || brand || maxP || minP;

  return (
    <main className="pb-24">
      <section className="relative h-64 overflow-hidden bg-black text-white sm:h-80">
        <img src={category.banner} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" />
        <div className="nv-container relative flex h-full flex-col justify-end pb-10">
          <p className="nv-eyebrow !text-[#c6ff00]">{all.length} {t("home.products")}</p>
          <h1 data-testid="category-title" className="mt-2 font-display text-4xl font-bold sm:text-5xl lg:text-6xl">{sub ? t(`subs.${sub}`) : t(`nav.${cat}`)}</h1>
          <p className="mt-2 max-w-xl text-sm text-white/70 md:text-base">{t(`catDesc.${cat}`)}</p>
        </div>
      </section>

      <div className="nv-container mt-6 flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        <button data-testid="sub-chip-all" onClick={() => set("sub", null)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${!sub ? "bg-black text-[#c6ff00]" : "bg-[#f4f5f0] hover:bg-black hover:text-white"}`}>{t("nav.all")}</button>
        {subsShown.map((s) => (
          <button key={s} data-testid={`sub-chip-${s}`} onClick={() => set("sub", s === sub ? null : s)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${sub === s ? "bg-black text-[#c6ff00]" : "bg-[#f4f5f0] hover:bg-black hover:text-white"}`}>{t(`subs.${s}`)}</button>
        ))}
      </div>

      <div className="nv-container mt-8 grid gap-6 lg:gap-10 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-8">
          <div className="flex items-center justify-between">
            <button type="button" data-testid="filters-toggle" onClick={() => setShowF(!showF)} className="inline-flex items-center gap-2 font-display font-bold lg:pointer-events-none">
              <SlidersHorizontal size={16} /> {t("plp.filters")}{hasFilters && <span className="grid h-5 w-5 place-items-center rounded-full bg-[#c6ff00] text-[10px]">{[sub, brand, maxP || minP].filter(Boolean).length}</span>}
              <ChevronDown size={16} className={`transition-transform lg:hidden ${showF ? "rotate-180" : ""}`} />
            </button>
            {hasFilters && <button data-testid="clear-filters" onClick={() => setSp(new URLSearchParams(sort !== "relevance" ? { sort } : {}))} className="inline-flex items-center gap-1 text-xs font-bold uppercase text-black/50 hover:text-black"><X size={12} /> {t("plp.clear")}</button>}
          </div>
          <div className={`${showF ? "block" : "hidden"} space-y-8 lg:block`}>
          <div>
            <p className="nv-eyebrow mb-3">{t("plp.brand")}</p>
            <ul className="space-y-1">
              {brandsAvail.map((b) => (
                <li key={b}>
                  <button data-testid={`brand-filter-${b.replace(/[^a-z0-9]/gi, "")}`} onClick={() => set("brand", b === brand ? null : b)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${brand === b ? "bg-[#c6ff00] font-bold" : "hover:bg-[#f4f5f0]"}`}>
                    {b} <span className="text-xs text-black/40">{brandCounts[b] || 0}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="nv-eyebrow mb-3">{t("plp.price")}</p>
            <SliderPrimitive.Root data-testid="price-range" min={0} max={top} step={1} minStepsBetweenThumbs={1} value={range} onValueChange={setRange} onValueCommit={commitRange} className="relative flex w-full touch-none select-none items-center py-2">
              <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-black/10">
                <SliderPrimitive.Range className="absolute h-full bg-[#c6ff00]" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb data-testid="price-thumb-min" aria-label="min" className="block h-5 w-5 cursor-grab rounded-full border-2 border-black bg-[#c6ff00] shadow transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30 active:cursor-grabbing" />
              <SliderPrimitive.Thumb data-testid="price-thumb-max" aria-label="max" className="block h-5 w-5 cursor-grab rounded-full border-2 border-black bg-[#c6ff00] shadow transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30 active:cursor-grabbing" />
            </SliderPrimitive.Root>
            <div className="mt-1 flex justify-between text-xs text-black/60"><span data-testid="price-min-label" className="font-bold">{fmtPrice(range[0], i18n.language)}</span><span data-testid="price-max-label" className="font-bold">{fmtPrice(range[1], i18n.language)}</span></div>
          </div>
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p data-testid="results-count" className="text-sm text-black/60"><b className="text-black">{list.length}</b> {t("plp.results")}</p>
            <Select value={sort} onValueChange={(v) => set("sort", v === "relevance" ? null : v)}>
              <SelectTrigger data-testid="sort-select" className="w-56 rounded-full border-black/15"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">{SORTS.map((s) => <SelectItem key={s} value={s} data-testid={`sort-${s}`}>{t(`plp.${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {list.length === 0 ? (
            <div data-testid="plp-empty" className="mt-16 rounded-2xl bg-[#f4f5f0] p-16 text-center">
              <p className="font-display text-xl font-bold">{t("plp.empty")}</p>
              <Link to={`/c/${cat}`} className="nv-btn-dark mt-6">{t("plp.clear")}</Link>
            </div>
          ) : (
            <div data-testid="product-grid" className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{list.map((p, i) => <ProductCard key={p.code} p={p} index={i} />)}</div>
          )}
        </div>
      </div>
    </main>
  );
}
